import { supabase } from '@/integrations/supabase/client';

type InvokeOptions = {
  body?: unknown;
  headers?: Record<string, string>;
};

const JWT_EXPIRY_BUFFER_SECONDS = 60;

const getStatusCode = (error: any): number | null => {
  if (typeof error?.status === 'number') return error.status;
  if (typeof error?.error?.status === 'number') return error.error.status;
  if (typeof error?.context?.status === 'number') return error.context.status;
  return null;
};

const payloadLooksLikeInvalidJwt = (payload: unknown) => {
  if (!payload || typeof payload !== 'object') return false;
  const code = (payload as any).code;
  const message = typeof (payload as any).message === 'string' ? (payload as any).message.toLowerCase() : '';
  return (code === 401 || code === '401') && message.includes('invalid jwt');
};

const errorLooksLikeInvalidJwt = (error: unknown) => {
  if (!error || typeof error !== 'object') return false;
  const status = getStatusCode(error);
  if (status === 401) return true;

  const details = [
    typeof (error as any).message === 'string' ? (error as any).message : '',
    typeof (error as any).details === 'string' ? (error as any).details : '',
    typeof (error as any).hint === 'string' ? (error as any).hint : '',
  ]
    .join(' ')
    .toLowerCase();

  return details.includes('invalid jwt') || (details.includes('jwt') && details.includes('invalid'));
};

const getBearerToken = async (forceRefresh = false): Promise<string | null> => {
  const nowSeconds = Math.floor(Date.now() / 1000);
  const { data, error } = forceRefresh
    ? await supabase.auth.refreshSession()
    : await supabase.auth.getSession();
  if (error) return null;
  const session = data.session;
  if (!session) return null;

  const expiresAt = typeof session.expires_at === 'number' ? session.expires_at : null;
  const shouldRefreshSoon =
    !forceRefresh &&
    typeof expiresAt === 'number' &&
    expiresAt - nowSeconds <= JWT_EXPIRY_BUFFER_SECONDS;

  if (shouldRefreshSoon) {
    const refreshed = await supabase.auth.refreshSession();
    if (!refreshed.error && refreshed.data.session?.access_token) {
      return refreshed.data.session.access_token;
    }
  }

  return session.access_token ?? null;
};

export async function invokeEdgeWithAuthRetry<T = any>(
  functionName: string,
  options: InvokeOptions = {},
) {
  const run = async (token: string) => {
    try {
      return await supabase.functions.invoke(functionName, {
        ...options,
        headers: {
          ...(options.headers || {}),
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      return {
        data: null as T | null,
        error,
      };
    }
  };

  let token = await getBearerToken(false);
  if (!token) token = await getBearerToken(true);
  if (!token) {
    return {
      data: null as T | null,
      error: { status: 401, message: 'Session expired or unavailable' },
    };
  }

  let result = await run(token);
  const shouldRetry =
    errorLooksLikeInvalidJwt(result.error) || payloadLooksLikeInvalidJwt(result.data);

  if (!shouldRetry) return result as { data: T | null; error: any };

  const refreshedToken = await getBearerToken(true);
  if (!refreshedToken) {
    return {
      data: null as T | null,
      error: { status: 401, message: 'Invalid JWT' },
    };
  }

  result = await run(refreshedToken);

  if (!result.error && payloadLooksLikeInvalidJwt(result.data)) {
    return {
      data: null as T | null,
      error: { status: 401, message: 'Invalid JWT' },
    };
  }

  return result as { data: T | null; error: any };
}
